import { Badge } from '@blogging/ui'
import { cn } from '@blogging/utils'
import {
  ArrowLeft,
  BookOpen,
  Bot,
  ExternalLink,
  Globe,
  Key,
  Menu,
  Navigation,
  Paintbrush,
  Palette,
  Puzzle,
  Settings,
  SlidersHorizontal,
  Users,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useSite } from '../../hooks/use-site'

type NavItem = {
  name: string
  href: string
  icon: React.ElementType
  badge?: string
  exact?: boolean
}

type NavGroup = {
  label: string
  items: NavItem[]
}

function navGroups(siteId: string, isAdmin: boolean): NavGroup[] {
  const url = (path: string) => `/${siteId}${path}`

  const groups: NavGroup[] = []

  // General section - all staff members can access, but some items are admin-only
  const generalItems: NavItem[] = [
    { name: 'General', href: url('/settings'), icon: Settings, exact: true },
  ]

  if (isAdmin) {
    generalItems.push({
      name: 'Staff',
      href: url('/settings/staff'),
      icon: Users,
    })
  }

  generalItems.push(
    { name: 'Design', href: url('/settings/design'), icon: Palette },
    { name: 'Theme', href: url('/settings/theme'), icon: Paintbrush },
  )

  if (isAdmin) {
    generalItems.push({
      name: 'Advanced',
      href: url('/settings/advanced'),
      icon: SlidersHorizontal,
    })
  }

  groups.push({
    label: 'General',
    items: generalItems,
  })

  // Content section - all staff can access
  groups.push({
    label: 'Content',
    items: [
      { name: 'Navigation', href: url('/settings/nav'), icon: Navigation },
    ],
  })

  // AI agents and developer sections - admin only (they manage API keys)
  if (isAdmin) {
    groups.push({
      label: 'AI Agents',
      items: [
        {
          name: 'MCP Server',
          href: url('/settings/mcp'),
          icon: Bot,
          badge: 'Beta',
        },
      ],
    })

    groups.push({
      label: 'Developer',
      items: [
        { name: 'Domains', href: url('/settings/domains'), icon: Globe },
        { name: 'API Keys', href: url('/settings/api-keys'), icon: Key },
        {
          name: 'API Reference',
          href: url('/settings/api-docs'),
          icon: BookOpen,
        },
        {
          name: 'Integrations',
          href: url('/settings/integrations'),
          icon: Puzzle,
          badge: 'Beta',
        },
      ],
    })
  }

  return groups
}

export function SettingSidebar() {
  const location = useLocation()
  const pathname = location.pathname
  const { siteId } = useParams()
  const { isLoading, siteUrlWithScheme, site, isAdmin } = useSite()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const isActive = (item: NavItem) => {
    if (item.exact) return pathname === item.href
    return pathname.startsWith(item.href)
  }

  const groups = isLoading ? [] : navGroups(siteId as string, isAdmin)

  const NavGroups = ({ onNavigate }: { onNavigate?: () => void }) => (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="px-2 mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                    isActive(item)
                      ? 'bg-secondary text-secondary-foreground'
                      : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.name}</span>
                  {item.badge && (
                    <Badge
                      variant="secondary"
                      className="ml-auto px-1.5 py-0 text-[10px]"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )

  return (
    <>
      {/* Mobile: sticky header bar + collapsible nav */}
      <div className="md:hidden sticky top-0 z-20 bg-background border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <Link
            to={`/${siteId}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">{site?.name ?? 'Back'}</span>
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground"
            aria-label="Toggle settings menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="border-t px-2 py-4">
            <NavGroups onNavigate={() => setIsMobileMenuOpen(false)} />
          </div>
        )}
      </div>

      {/* Desktop: vertical sidebar */}
      <div className="hidden md:flex w-56 shrink-0 border-r sticky top-0 h-screen overflow-y-auto flex-col">
        {/* Header */}
        <div className="px-4 py-4 border-b">
          <Link
            to={`/${siteId}`}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium truncate">{site?.name ?? 'Back'}</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4">
          <NavGroups />
        </nav>

        {/* Footer */}
        {siteUrlWithScheme && (
          <div className="px-2 py-4 border-t">
            <a
              href={siteUrlWithScheme}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-4 w-4 shrink-0" />
              <span>Preview site</span>
            </a>
          </div>
        )}
      </div>
    </>
  )
}
